import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    inject
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, CachedValuesService, ToastService, RightPanelService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetFirmwareVersionResponse } from 'app/types/firmware-version.type';
import { FirmwareFile } from '@services/api-client/responses';
import { Observable, finalize, map, take } from 'rxjs';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { RegionModel } from 'app/types/region.type';

type CreateFirmwareForm = {
    name: FormControl<string>;
    version: FormControl<string>;
    description: FormControl<string>;
    releaseNotes: FormControl<string>;
    releaseDate: FormControl<string>;
    regionIds: FormControl<string[]>;
    allUsers: FormControl<boolean>;
    userIds: FormControl<string[]>;
};

type FirmwareFileUpload = {
    file: File;
    uploadOrder: number;
};

type ExistingFileWithRemoveFlag = {
    file: FirmwareFile;
    removed: boolean;
};

type DisplayFile = {
    name: string;
    size: number;
    uploadOrder?: number;
    id?: string;
    isExisting: boolean;
    isRemoved: boolean;
    existingIndex?: number;
    uploadedIndex?: number;
    displayOrder: number;
};

@UntilDestroy()
@Component({
    selector: 'app-firmware-detail-panel',
    templateUrl: './firmware-detail-panel.component.html',
    styleUrls: ['./firmware-detail-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirmwareDetailPanelComponent implements OnInit, OnChanges {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly rightPanelService = inject(RightPanelService);

    @Input() public firmwareId?: string;

    public firmware?: GetFirmwareVersionResponse;

    public isEditMode = false;
    public showPotentialValidationMessages = false;
    public loading = false;
    public firmwareForm: FormGroup<CreateFirmwareForm>;
    public dragOver = false;
    public cachedRegionIds: string[] = [];
    public cachedAllRegions: MultiSelectItem[] = [];
    public cachedAllUsers: MultiSelectItem[] = [];
    public usersLoading = true;
    public usersLoadFailed = false;
    private sortedFiles: FirmwareFile[] = [];
    private existingFiles: ExistingFileWithRemoveFlag[] = [];
    private uploadedFiles: FirmwareFileUpload[] = [];
    private fileDisplayOrder: { isExisting: boolean; index: number }[] = [];

    public readonly regions$: Observable<DropdownItem<string>[]> = this.cachedValuesService.regions$.pipe(
        map((data) =>
            data.map((item: RegionModel) => {
                return {
                    label: item.name,
                    value: item.id
                } as DropdownItem<string>;
            })
        )
    );

    public get f() {
        return this.firmwareForm.controls;
    }

    public get selectedUserNames(): string[] {
        return (this.firmware?.userIds ?? []).map(
            (id) => this.cachedAllUsers.find((user) => user.id === id)?.name ?? id
        );
    }

    public get uploadedFilesList(): FirmwareFileUpload[] {
        return this.uploadedFiles;
    }

    public get existingFilesList(): ExistingFileWithRemoveFlag[] {
        return this.existingFiles;
    }

    public get canManageFiles(): boolean {
        return this.isEditMode && !this.firmware;
    }

    public getAllFilesList(): DisplayFile[] {
        // Create maps of all files
        const existingMap = this.existingFiles.map(
            (f, idx) =>
                ({
                    name: f.file.name,
                    size: f.file.size,
                    uploadOrder: f.file.uploadOrder,
                    id: f.file.id,
                    isExisting: true,
                    isRemoved: f.removed,
                    existingIndex: idx,
                    displayOrder: 0
                }) as DisplayFile
        );

        const uploadedMap = this.uploadedFiles.map(
            (f, idx) =>
                ({
                    name: f.file.name,
                    size: f.file.size,
                    uploadOrder: f.uploadOrder,
                    id: undefined,
                    isExisting: false,
                    isRemoved: false,
                    uploadedIndex: idx,
                    displayOrder: 0
                }) as DisplayFile
        );

        // Return files in display order, excluding removed files
        return this.fileDisplayOrder
            .map((order) => {
                if (order.isExisting) {
                    return existingMap[order.index];
                } else {
                    return uploadedMap[order.index];
                }
            })
            .filter((f) => !f.isRemoved);
    }

    private initializeFileDisplayOrder(): void {
        this.fileDisplayOrder = [
            ...this.existingFiles.map((_, idx) => ({ isExisting: true, index: idx })),
            ...this.uploadedFiles.map((_, idx) => ({ isExisting: false, index: idx }))
        ];
    }

    public ngOnInit() {
        const isCreate = !this.firmwareId;
        const fb = new FormBuilder();
        this.firmwareForm = fb.nonNullable.group({
            name: ['', [Validators.required]],
            version: ['', [Validators.required]],
            description: ['', [Validators.required]],
            releaseNotes: ['', [Validators.required]],
            releaseDate: [new Date().toISOString().split('T')[0], [Validators.required]],
            regionIds: fb.nonNullable.control<string[]>([], [Validators.required]),
            allUsers: fb.nonNullable.control(true),
            userIds: fb.nonNullable.control<string[]>([])
        });

        this.f.allUsers.valueChanges.pipe(untilDestroyed(this)).subscribe((allUsers) => {
            this.updateUserSelectionValidation(allUsers);
        });

        this.cachedValuesService.users$.pipe(take(1), untilDestroyed(this)).subscribe({
            next: (users) => {
                this.cachedAllUsers = users.map((user) => ({
                    id: user.id,
                    name: `${user.firstName} ${user.lastName} (${user.email})`
                }));
                this.usersLoading = false;
                this.changeDetectorRef.markForCheck();
            },
            error: (error) => {
                console.error('Error fetching firmware users:', error);
                this.usersLoading = false;
                this.usersLoadFailed = true;
                this.changeDetectorRef.markForCheck();
            }
        });

        // Start in edit mode if creating new firmware, otherwise start in readonly mode
        this.isEditMode = isCreate;

        // Load all available regions for multi-select
        this.cachedValuesService.regions$.pipe(take(1), untilDestroyed(this)).subscribe((regions) => {
            this.cachedAllRegions = regions.map((r) => ({
                id: r.id,
                name: r.name
            }));
        });

        // Fetch firmware data from backend if editing
        if (this.firmwareId) {
            this.loading = true;
            this.apiClientService
                .getFirmwareVersion$(this.firmwareId)
                .pipe(take(1), untilDestroyed(this))
                .subscribe({
                    next: (fullFirmware) => {
                        this.firmware = fullFirmware;
                        this.sortedFiles = this.getSortedFiles(fullFirmware.files);
                        const sortedByOrder = this.getSortedFiles(fullFirmware.files);
                        this.existingFiles = sortedByOrder.map((file) => ({ file, removed: false }));
                        this.cachedRegionIds = fullFirmware.regions.map((region) => region.id);
                        const releaseDateValue = fullFirmware.releaseDate ? fullFirmware.releaseDate.split('T')[0] : '';
                        this.firmwareForm.patchValue({
                            name: fullFirmware.name,
                            version: fullFirmware.version,
                            description: fullFirmware.description,
                            releaseNotes: fullFirmware.releaseNotes,
                            releaseDate: releaseDateValue,
                            regionIds: this.cachedRegionIds,
                            allUsers: fullFirmware.userIds.length === 0,
                            userIds: fullFirmware.userIds
                        });
                        this.initializeFileDisplayOrder();
                        this.loading = false;
                        this.changeDetectorRef.markForCheck();
                    },
                    error: (error) => {
                        console.error('Error fetching firmware:', error);
                        this.loading = false;
                        this.changeDetectorRef.markForCheck();
                    }
                });
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['firmwareId']) {
            const firmwareId = changes['firmwareId'].currentValue as string | undefined;

            if (firmwareId) {
                // Fetch full firmware data from backend
                this.loading = true;
                this.apiClientService
                    .getFirmwareVersion$(firmwareId)
                    .pipe(take(1), untilDestroyed(this))
                    .subscribe({
                        next: (fullFirmware) => {
                            this.firmware = fullFirmware;
                            this.sortedFiles = this.getSortedFiles(fullFirmware.files);
                            const sortedByOrder = this.getSortedFiles(fullFirmware.files);
                            this.existingFiles = sortedByOrder.map((file) => ({ file, removed: false }));
                            this.cachedRegionIds = fullFirmware.regions.map((region) => region.id);
                            const releaseDateValue = fullFirmware.releaseDate
                                ? fullFirmware.releaseDate.split('T')[0]
                                : '';
                            this.firmwareForm.patchValue({
                                name: fullFirmware.name,
                                version: fullFirmware.version,
                                description: fullFirmware.description,
                                releaseNotes: fullFirmware.releaseNotes,
                                releaseDate: releaseDateValue,
                                regionIds: this.cachedRegionIds,
                                allUsers: fullFirmware.userIds.length === 0,
                                userIds: fullFirmware.userIds
                            });
                            this.initializeFileDisplayOrder();
                            this.loading = false;
                            this.changeDetectorRef.markForCheck();
                        },
                        error: (error) => {
                            console.error('Error fetching firmware:', error);
                            this.loading = false;
                            this.changeDetectorRef.markForCheck();
                        }
                    });
            }
        }
    }

    public enterEditMode(): void {
        this.isEditMode = true;
        this.changeDetectorRef.markForCheck();
    }

    public cancelEdit(): void {
        if (this.firmware) {
            this.isEditMode = false;
            this.showPotentialValidationMessages = false;
            this.uploadedFiles = [];
            const sortedByOrder = this.getSortedFiles(this.firmware.files);
            this.existingFiles = sortedByOrder.map((file) => ({ file, removed: false }));
            this.initializeFileDisplayOrder();
            this.firmwareForm.patchValue({
                name: this.firmware.name,
                version: this.firmware.version,
                description: this.firmware.description,
                releaseNotes: this.firmware.releaseNotes,
                releaseDate: this.firmware.releaseDate,
                regionIds: this.firmware.regions?.map((r) => r.id) || [],
                allUsers: this.firmware.userIds.length === 0,
                userIds: this.firmware.userIds
            });
        } else {
            this.rightPanelService.hide();
        }
        this.changeDetectorRef.markForCheck();
    }

    public saveAndExitEdit(): void {
        this.showPotentialValidationMessages = true;
        this.changeDetectorRef.markForCheck();

        if (this.firmwareForm.invalid || (!this.f.allUsers.value && (this.usersLoading || this.usersLoadFailed))) {
            return;
        }

        // When updating, files are optional. When creating, at least one file is required
        const isCreate = !this.firmware;
        if (isCreate && this.uploadedFiles.length === 0) {
            return;
        }

        this.loading = true;
        this.changeDetectorRef.markForCheck();

        // Recalculate upload orders based on current display order
        this.recalculateFileUploadOrders();

        const formData = new FormData();
        formData.append('name', this.f.name.value);
        formData.append('version', this.f.version.value);
        formData.append('description', this.f.description.value);
        formData.append('releaseNotes', this.f.releaseNotes.value || '');
        formData.append('releaseDate', this.f.releaseDate.value);
        formData.append('regionIds', JSON.stringify(this.f.regionIds.value));
        formData.append('userIds', JSON.stringify(this.f.allUsers.value ? [] : this.f.userIds.value));

        if (isCreate) {
            // Add uploaded files in display order (backend will assign uploadOrder 1, 2, 3... in this order)
            this.getAllFilesList().forEach((displayFile) => {
                const uploadedFile = this.uploadedFiles.find((uf, idx) => idx === displayFile.uploadedIndex);
                if (uploadedFile) {
                    formData.append('files', uploadedFile.file);
                }
            });
        } else {
            // When editing, send the new upload orders for existing files
            const fileUploadOrders: Record<string, number> = {};
            this.existingFiles.forEach((f) => {
                if (!f.removed) {
                    fileUploadOrders[f.file.id] = f.file.uploadOrder;
                }
            });
            formData.append('fileUploadOrders', JSON.stringify(fileUploadOrders));
        }

        // Add existing files that should be kept (for reference)
        const filesToKeep = this.existingFiles.filter((f) => !f.removed).map((f) => f.file.id);
        formData.append('existingFileIds', JSON.stringify(filesToKeep));

        const observable$ = isCreate
            ? this.apiClientService.createFirmwareVersion(formData)
            : this.apiClientService.updateFirmwareVersion(this.firmware.id, formData);

        observable$
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.changeDetectorRef.markForCheck();
                }),
                take(1),
                untilDestroyed(this)
            )
            .subscribe({
                next: () => {
                    this.loading = false;
                    this.showPotentialValidationMessages = false;
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        isCreate
                            ? 'FEATURES.FIRMWARE.TOAST_MESSAGES.CREATE_SUCCESS'
                            : 'FEATURES.FIRMWARE.TOAST_MESSAGES.UPDATE_SUCCESS'
                    ) as string;
                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.cachedValuesService.invalidateCache();
                    this.rightPanelService.hide();
                },
                error: (error) => {
                    console.error('Error saving firmware:', error);
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        isCreate
                            ? 'FEATURES.FIRMWARE.TOAST_MESSAGES.CREATE_ERROR'
                            : 'FEATURES.FIRMWARE.TOAST_MESSAGES.UPDATE_ERROR'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    private updateUserSelectionValidation(allUsers: boolean): void {
        if (allUsers) {
            this.f.userIds.clearValidators();
            this.f.userIds.setValue([]);
        } else {
            this.f.userIds.setValidators(Validators.required);
        }
        this.f.userIds.updateValueAndValidity();
    }

    public onFilesSelected(files: FileList | null): void {
        if (!files) return;
        this.addFiles(Array.from(files));
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = true;
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;
        if (event.dataTransfer?.files) {
            this.addFiles(Array.from(event.dataTransfer.files));
        }
    }

    private addFiles(files: File[]): void {
        const startOrder = this.uploadedFiles.length + 1;
        files.forEach((file, index) => {
            this.uploadedFiles.push({
                file,
                uploadOrder: startOrder + index
            });
        });
        this.initializeFileDisplayOrder();
        this.changeDetectorRef.markForCheck();
    }

    public removeFile(index: number): void {
        this.uploadedFiles.splice(index, 1);
        this.reorderFiles();
        this.initializeFileDisplayOrder();
        this.changeDetectorRef.markForCheck();
    }

    public removeExistingFile(index: number): void {
        if (index >= 0 && index < this.existingFiles.length) {
            this.existingFiles[index].removed = !this.existingFiles[index].removed;
            this.initializeFileDisplayOrder();
            this.changeDetectorRef.markForCheck();
        }
    }

    public moveFileUp(allFiles: DisplayFile[], index: number): void {
        if (index <= 0) return;

        const currentFile = allFiles[index];
        const previousFile = allFiles[index - 1];

        // Find actual positions in fileDisplayOrder
        const currentPos = this.fileDisplayOrder.findIndex(
            (order) =>
                order.isExisting === currentFile.isExisting &&
                order.index === (currentFile.isExisting ? currentFile.existingIndex : currentFile.uploadedIndex)
        );
        const previousPos = this.fileDisplayOrder.findIndex(
            (order) =>
                order.isExisting === previousFile.isExisting &&
                order.index === (previousFile.isExisting ? previousFile.existingIndex : previousFile.uploadedIndex)
        );

        if (currentPos > 0 && previousPos >= 0) {
            [this.fileDisplayOrder[currentPos], this.fileDisplayOrder[previousPos]] = [
                this.fileDisplayOrder[previousPos],
                this.fileDisplayOrder[currentPos]
            ];
            this.changeDetectorRef.markForCheck();
        }
    }

    public moveFileDown(allFiles: DisplayFile[], index: number): void {
        if (index >= allFiles.length - 1) return;

        const currentFile = allFiles[index];
        const nextFile = allFiles[index + 1];

        // Find actual positions in fileDisplayOrder
        const currentPos = this.fileDisplayOrder.findIndex(
            (order) =>
                order.isExisting === currentFile.isExisting &&
                order.index === (currentFile.isExisting ? currentFile.existingIndex : currentFile.uploadedIndex)
        );
        const nextPos = this.fileDisplayOrder.findIndex(
            (order) =>
                order.isExisting === nextFile.isExisting &&
                order.index === (nextFile.isExisting ? nextFile.existingIndex : nextFile.uploadedIndex)
        );

        if (currentPos >= 0 && nextPos > currentPos) {
            [this.fileDisplayOrder[currentPos], this.fileDisplayOrder[nextPos]] = [
                this.fileDisplayOrder[nextPos],
                this.fileDisplayOrder[currentPos]
            ];
            this.changeDetectorRef.markForCheck();
        }
    }

    public removeFileFromList(allFiles: DisplayFile[], index: number): void {
        const file = allFiles[index];
        if (file.isExisting && file.existingIndex !== undefined) {
            this.removeExistingFile(file.existingIndex);
        } else if (!file.isExisting && file.uploadedIndex !== undefined) {
            this.removeFile(file.uploadedIndex);
        }
    }

    private recalculateFileUploadOrders(): void {
        const allFiles = this.getAllFilesList();
        allFiles.forEach((displayFile, index) => {
            if (displayFile.isExisting && displayFile.existingIndex !== undefined) {
                this.existingFiles[displayFile.existingIndex].file.uploadOrder = index + 1;
            } else if (!displayFile.isExisting && displayFile.uploadedIndex !== undefined) {
                this.uploadedFiles[displayFile.uploadedIndex].uploadOrder = index + 1;
            }
        });
    }

    private reorderFiles(): void {
        this.uploadedFiles.forEach((file, index) => {
            file.uploadOrder = index + 1;
        });
    }

    public formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }

    public getSortedFiles(files: FirmwareFile[]): FirmwareFile[] {
        if (!files || files.length === 0) {
            return [];
        }
        return [...files].sort((a, b) => (a.uploadOrder ?? 0) - (b.uploadOrder ?? 0));
    }

    public getDisplayFiles(): FirmwareFile[] {
        if (!this.firmware?.files) return [];
        return this.getSortedFiles(this.firmware.files);
    }

    public close(): void {
        this.rightPanelService.hide();
    }
}
